import {
  ButtonProps,
  IconButtonProps,
  LinkProps,
  LinkOwnProps,
  SxProps,
  Theme,
  TextFieldProps,
} from '@mui/material';
import { LinkProps as RouterLinkProps } from 'react-router-dom';
import useGetSubscription from 'helpers/useGetSubscription';
import { ReactElement, cloneElement } from 'react';
import { SubscriptionStatus } from 'store/subscription/interfaces';

interface GuardProps {
  button: Pick<ButtonProps, 'disabled' | 'sx'>;
  iconButton: Pick<IconButtonProps, 'disabled' | 'sx'>;
  link: Pick<LinkProps, 'sx'>;
}

const expiredSubscriptionProps: GuardProps = {
  button: {
    disabled: true,
    sx: {
      '& .MuiSvgIcon-root': {
        color: 'text.disabled',
      },
    },
  },
  iconButton: {
    disabled: true,
    sx: {
      pointerEvents: 'none',
      userSelect: 'none',

      '& .MuiSvgIcon-root': {
        color: 'text.disabled',
      },
    },
  },
  link: {
    sx: {
      pointerEvents: 'none',
      userSelect: 'none',
      color: 'text.disabled',

      '&:visited': {
        color: 'text.disabled',
      },
    },
  },
};

export default function useSubscriptionGuard() {
  const { subscription } = useGetSubscription();

  const isSubscriptionExpired =
    subscription?.status === SubscriptionStatus.cancelled;

  const guardLink = (
    link: ReactElement<RouterLinkProps & Omit<LinkOwnProps, 'component'>>,
  ) =>
    isSubscriptionExpired
      ? cloneElement(link, {
          ...link.props,
          sx: {
            ...link.props.sx,
            ...expiredSubscriptionProps.link.sx,
          } as SxProps<Theme> | undefined,
        })
      : link;

  const guardButton = (
    button: ReactElement<ButtonProps>,
  ): ReactElement<ButtonProps> =>
    isSubscriptionExpired
      ? cloneElement(button, {
          ...button.props,
          disabled: true,
          sx: {
            ...button.props.sx,
            ...expiredSubscriptionProps.button.sx,
          } as SxProps<Theme> | undefined,
        })
      : button;

  const guardIconButton = (
    iconButton: ReactElement<IconButtonProps>,
  ): ReactElement<IconButtonProps> =>
    isSubscriptionExpired
      ? cloneElement(iconButton, {
          ...iconButton.props,
          disabled: true,
          sx: {
            ...iconButton.props.sx,
            ...expiredSubscriptionProps.iconButton.sx,
          } as SxProps<Theme> | undefined,
        })
      : iconButton;

  const guardInput = (
    input: ReactElement<TextFieldProps>,
  ): ReactElement<TextFieldProps> =>
    isSubscriptionExpired
      ? cloneElement(input, {
          ...input.props,
          disabled: true,
        })
      : input;

  return {
    guardLink,
    guardButton,
    guardIconButton,
    isSubscriptionExpired,
    guardInput,
  };
}
